/**
 * `useLeaveReviewQueue` + the three per-row actions — the lead's Requests
 * review queue (#1670, design §8.2).
 *
 * Backed by `backend/src/api/routes/leave_review.py`:
 *
 *   * `GET  /api/cycles/{cycleId}/leave-review`                      — the
 *     queue, already ordered urgency → category priority → submitted-at.
 *     The SPA renders that order; it never re-sorts (the server owns §8.2).
 *   * `POST /api/cycles/{cycleId}/leave-review/{leaveId}/accept`      — the
 *     lead touch. Urgent rows come back with a `cover_handoff`; the SPA no
 *     longer consumes it (#1952 retired the mc-recover panel it fed).
 *   * `POST /api/cycles/{cycleId}/leave-review/{leaveId}/decline`     —
 *     structured reason, required.
 *   * `POST /api/cycles/{cycleId}/leave-review/{leaveId}/ask-to-move`  —
 *     writes `superseded` and cards the member.
 *
 * Review is OPTIONAL: none of this gates a solve. The batch-default copy the
 * queue renders comes down the wire in `batch_default_note`, so the calm
 * posture is stated once, on the server, and cannot drift here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { invalidateRosterReads } from "./rosterInvalidation";
import type { components } from "./schema.gen";

export type LeaveReviewQueue = components["schemas"]["LeaveReviewQueueResponse"];
export type LeaveReviewRow = components["schemas"]["LeaveReviewRowOut"];
export type LeaveReviewConflict = components["schemas"]["LeaveReviewConflictOut"];
export type LeaveReviewAction = components["schemas"]["LeaveReviewActionResponse"];
export type LeaveCoverHandoff = components["schemas"]["LeaveReviewCoverHandoff"];
export type DeclineReasonCode = components["schemas"]["LeaveDeclineBody"]["reason_code"];

/** Lead-facing labels for the structured decline reasons. The member never
 *  sees these — they read the composed sentence the server writes. */
export const DECLINE_REASON_LABELS: Record<DeclineReasonCode, string> = {
  category_priority: "Another type took priority",
  coverage: "Cover on these dates",
  seniority_window: "Seniority window",
  other: "Something else",
};

/** What the member will be told, shown to the lead BEFORE they commit —
 *  mirrors `leavestate.review._DECLINE_SENTENCES` so the lead is never
 *  surprised by the words their decision puts in someone's Inbox. */
export const DECLINE_REASON_PREVIEW: Record<DeclineReasonCode, string> = {
  category_priority: "Another category of request took priority for these dates.",
  coverage: "The team would have been short of cover on these dates.",
  seniority_window: "These dates fall inside a window held for a different seniority group.",
  other: "",
};

export const LEAVE_REVIEW_KEY = (cycleId: string) => ["leave-review", cycleId] as const;

/** One roster's queue read: the key, the fetch and the cache rules. Shared by
 *  `useLeaveReviewQueue` and the phone Review's read of every roster ahead
 *  (`memberapp/team/useLeaveQueuesAhead.ts`), so both use one cache entry. */
export function leaveReviewQueueQuery(cycleId: string | undefined, accountId: string | null) {
  return {
    queryKey: [...LEAVE_REVIEW_KEY(cycleId ?? ""), accountId ?? ""],
    enabled: Boolean(cycleId && accountId),
    retry: false,
    staleTime: 30_000,
    queryFn: () => apiFetch<LeaveReviewQueue>(`/api/cycles/${cycleId}/leave-review`),
  } as const;
}

export function useLeaveReviewQueue(cycleId: string | undefined, enabled = true) {
  const { accountId } = useAuth();
  const query = leaveReviewQueueQuery(cycleId, accountId);
  return useQuery({ ...query, enabled: query.enabled && enabled });
}

/** Every action invalidates the queue AND the roster-wide date-request view —
 *  a decline or an ask-to-move changes what the List/Grid tabs show too.
 *  #3075: every roster's queue, not only this one's. A request across two
 *  rosters sits in both queues, and the phone Review lists it once. */
function useReviewAction<TBody>(cycleId: string | undefined, path: string) {
  const queryClient = useQueryClient();
  return useMutation<LeaveReviewAction, ApiError | Error, { leaveId: string; body?: TBody }>({
    mutationFn: ({ leaveId, body }) =>
      apiFetch<LeaveReviewAction>(`/api/cycles/${cycleId}/leave-review/${leaveId}/${path}`, {
        method: "POST",
        body: JSON.stringify(body ?? {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-review"] });
      queryClient.invalidateQueries({ queryKey: ["team-date-requests"] });
      // #2277 — a decline or an ask-to-move takes the row out of the quota count.
      queryClient.invalidateQueries({ queryKey: ["leave-quota"] });
      // #2173 — the grid and the day verdict's *off* read the leave too.
      void invalidateRosterReads(queryClient, cycleId);
    },
  });
}

export function useAcceptLeaveIntoPlan(cycleId: string | undefined) {
  return useReviewAction<never>(cycleId, "accept");
}

export function useDeclineLeaveRequest(cycleId: string | undefined) {
  return useReviewAction<{ reason_code: DeclineReasonCode; note?: string | null }>(
    cycleId,
    "decline",
  );
}

export function useAskToMoveLeaveRequest(cycleId: string | undefined) {
  return useReviewAction<{ note?: string | null }>(cycleId, "ask-to-move");
}
